import { CalculatorInputs, CalculationOutput } from "../types";
import { supabase } from "./supabase";
import { User } from "@supabase/supabase-js";

export interface SimulationRecord {
  id: string;
  clientName: string;
  clientNif: string;
  createdAt: string;
  year: number;
  region: string;
  brutoFacturado: number;
  netoFinalTrasRenta: number;
  cajaPreviaRenta: number;
  inputs: CalculatorInputs;
  output: CalculationOutput;
}

const STORAGE_KEY = "autonomoClaro_simulaciones";

export async function loadSimulations(user: User | null): Promise<SimulationRecord[]> {
  if (user) {
    try {
      const { data, error } = await supabase
        .from("simulaciones")
        .select("*")
        .eq("gestoria_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return (data || []).map((row: any) => ({
        id: row.id,
        clientName: row.client_name,
        clientNif: row.client_nif || "",
        createdAt: row.created_at,
        year: row.inputs?.year || 2025,
        region: row.inputs?.region || "general",
        brutoFacturado: row.bruto_anual || 0,
        netoFinalTrasRenta: row.output_neto || 0,
        cajaPreviaRenta: 0,
        inputs: row.inputs,
        output: {} as CalculationOutput,
      }));
    } catch (error) {
      console.error("Error loading from Supabase:", error);
      return loadSimulationsFromLocalStorage();
    }
  }

  return loadSimulationsFromLocalStorage();
}

function loadSimulationsFromLocalStorage(): SimulationRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as SimulationRecord[];
  } catch {
    return [];
  }
}

export async function saveSimulation(rec: SimulationRecord, user: User | null) {
  if (user) {
    try {
      const { error } = await supabase.from("simulaciones").insert({
        gestoria_id: user.id,
        client_name: rec.clientName,
        client_nif: rec.clientNif,
        inputs: rec.inputs,
        bruto_anual: rec.brutoFacturado,
        output_neto: rec.netoFinalTrasRenta,
        notes: "",
      });

      if (error) throw error;
      return;
    } catch (error) {
      console.error("Error saving to Supabase:", error);
      throw error;
    }
  }

  if (typeof window === "undefined") return;
  const all = loadSimulationsFromLocalStorage();
  all.unshift(rec);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(all.slice(0, 200)));
}

export async function deleteSimulation(id: string, user: User | null) {
  if (user) {
    try {
      const { error } = await supabase.from("simulaciones").delete().eq("id", id);

      if (error) throw error;
      return;
    } catch (error) {
      console.error("Error deleting from Supabase:", error);
    }
  }

  if (typeof window === "undefined") return;
  const all = loadSimulationsFromLocalStorage();
  const filtered = all.filter((s) => s.id !== id);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
}
